import type { ApiResponse } from "@/lib/api/types";

import { getLogger } from "./logger";
import { apiFailureMessage, isErrorResponse, normalizedHubErrorDetails } from "./response";

const logger = getLogger("hub-api");

export type HubApiErrorLogFields = {
  message: string;
  hubCode?: string;
  hubReason?: string;
  status?: number;
  requestId?: string;
  path?: string;
  method?: string;
};

function detailsRecord(error: unknown): Record<string, unknown> {
  if (error !== null && typeof error === "object" && "success" in error) {
    return normalizedHubErrorDetails(error as ApiResponse<unknown>);
  }
  return {};
}

export function hubApiErrorFields(error: unknown, hints?: { path?: string; method?: string }): HubApiErrorLogFields {
  const details = detailsRecord(error);
  const status = typeof details.status === "number" ? details.status : undefined;
  const hubCode = typeof details.hub_code === "string" ? details.hub_code.trim() : "";
  const hubReason = typeof details.hub_reason === "string" ? details.hub_reason.trim() : "";
  let requestId = "";
  if (
    error !== null &&
    typeof error === "object" &&
    "meta" in error &&
    typeof (error as { meta?: { request_id?: unknown } }).meta?.request_id === "string"
  ) {
    requestId = String((error as { meta: { request_id: string } }).meta.request_id).trim();
  }

  let message = "";
  if (error !== null && typeof error === "object" && "success" in error) {
    message = apiFailureMessage(error as ApiResponse<unknown>);
  } else if (error instanceof Error) {
    message = error.message.trim();
  } else if (typeof error === "string") {
    message = error.trim();
  }

  return {
    message: message.length > 0 ? message : "Request failed.",
    hubCode: hubCode.length > 0 ? hubCode : undefined,
    hubReason: hubReason.length > 0 ? hubReason : undefined,
    status,
    requestId: requestId.length > 0 ? requestId : undefined,
    path: hints?.path?.trim() ? hints.path.trim() : undefined,
    method: hints?.method?.trim() ? hints.method.trim() : undefined,
  };
}

/** Log a caught API/client error with optional UI action context (e.g. projects.saveProject). */
export function logHubApiError(source: string, error: unknown, hints?: { path?: string; method?: string }): void {
  const fields = hubApiErrorFields(error, hints);
  if (fields.status === 401) return;
  logger.warn(`[${source}]`, fields);
}

/** Log a non-throwing API response envelope failure. */
export function logHubApiErrorResponse(source: string, res: ApiResponse<unknown>): void {
  if (!isErrorResponse(res)) return;
  logHubApiError(source, res);
}
